// ════════════════════════════════════════════════════════════════════
// feedKit — style tokens and tiny presentational pieces for the
// integration feed screens (AdminFeed, FeedMapping, FeedConflicts,
// FeedDialogs). Deliberately free of lib/feedApi, so the editor components
// render in tests without the API client.
//
// The first block mirrors the module-private admin tokens in App.js
// (inputStyle … btnPage, ~line 3330), which a file outside App.js can't
// import; AdminSubscriptions.js and ConnectPlatforms.js keep local copies the
// same way. Keep them in step with App.js. The chip and banner tones reuse
// ConnectPlatforms' STATUS_STYLE palette and App.js ErrorBlock. No new
// design system.
// ════════════════════════════════════════════════════════════════════
import { useT } from "../lib/i18n";
import { feedErrorCode, feedErrorKey, isKnownFeedError } from "../lib/feedUi";

// ── mirrors App.js ──────────────────────────────────────────────────
export const inputStyle = {
  padding:"8px 11px", borderRadius:7, border:"1px solid #e2e8f0",
  background:"#fff", fontSize:13, fontFamily:"inherit", color:"#0f172a",
  boxSizing:"border-box",
};
export const selectStyleAdmin = {
  padding:"8px 11px", borderRadius:7, border:"1px solid #e2e8f0",
  background:"#fff", fontSize:13, fontFamily:"inherit", color:"#0f172a",
};
export const adminThStyle = {
  padding:"10px 14px", textAlign:"start", fontSize:10, fontWeight:700,
  color:"#64748b", textTransform:"uppercase", letterSpacing:".7px",
  borderBottom:"1px solid #e2e8f0",
};
export const adminTdStyle = { padding:"12px 14px", verticalAlign:"middle" };
export const codeStyle = {
  background:"#f1f5f9", padding:"1px 5px", borderRadius:3, fontSize:11,
  fontVariantNumeric:"tabular-nums", color:"#475569",
};
export const btnPrimaryDark = {
  padding:"8px 16px", borderRadius:7, border:"none",
  background:"linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)",
  color:"#fff", cursor:"pointer", fontSize:13, fontFamily:"inherit",
  fontWeight:600, letterSpacing:"-.1px", boxShadow:"0 2px 6px rgba(15,23,42,.18)",
};
export const btnSecondary = {
  padding:"8px 14px", borderRadius:7, border:"1px solid #e2e8f0",
  background:"#fff", color:"#0f172a", cursor:"pointer", fontSize:12.5,
  fontFamily:"inherit", fontWeight:600, letterSpacing:"-.1px",
  boxShadow:"0 1px 2px rgba(15,23,42,.04)",
};
export const btnGhostDanger = {
  padding:"5px 12px", borderRadius:6, border:"1px solid #fecaca",
  background:"#fff", color:"#991b1b", cursor:"pointer", fontSize:11.5,
  fontFamily:"inherit", fontWeight:600,
};
export const btnPage = (disabled) => ({
  padding:"6px 12px", borderRadius:6, border:"1px solid #e2e8f0",
  background:"#fff", color: disabled ? "#cbd5e1" : "#475569",
  cursor: disabled ? "not-allowed" : "pointer", fontSize:12,
  fontFamily:"inherit", opacity: disabled ? .6 : 1, fontWeight:600,
});

// ── feed-local helpers built from the tokens above ──────────────────
// A button in its disabled state (dimmed, no pointer).
export const disabledLook = (style, disabled) => (disabled ? { ...style, opacity: .55, cursor: "not-allowed" } : style);

export const smallBtn = { ...btnSecondary, padding:"5px 11px", fontSize:11.5 };

const TONES = {
  green: { bg:"#e6f4ec", fg:"#0a7d4b", border:"#bfe3cf" },
  blue:  { bg:"#e8f0fb", fg:"#1e528f", border:"#c8daf3" },
  amber: { bg:"#fdf0dd", fg:"#9a6b10", border:"#f3d9a8" },
  red:   { bg:"#fbe6e2", fg:"#a3372a", border:"#f3c3b9" },
  grey:  { bg:"#eef1f4", fg:"#5b6773", border:"#dde3e9" },
};
export function chipStyle(tone) {
  const c = TONES[tone] || TONES.grey;
  return {
    display:"inline-block", fontSize:10.5, fontWeight:700, letterSpacing:".3px",
    padding:"2px 8px", borderRadius:999, background:c.bg, color:c.fg,
    border:`1px solid ${c.border}`, whiteSpace:"nowrap", lineHeight:1.5,
  };
}
// Banners: "error" mirrors App.js ErrorBlock; the others use the chip tones.
export function bannerStyle(kind) {
  const c = kind === "error" ? { bg:"#fef2f2", fg:"#991b1b", border:"#fecaca" }
    : kind === "warn" ? TONES.amber
    : kind === "ok" ? TONES.green
    : TONES.blue;
  return {
    padding:"10px 14px", borderRadius:7, fontSize:13, lineHeight:1.5,
    background:c.bg, color:c.fg, border:`1px solid ${c.border}`,
  };
}
export const mutedText = { fontSize:11.5, color:"#94a3b8" };
export const labelStyle = { display:"block", fontSize:11, fontWeight:700, color:"#475569", marginBottom:5, letterSpacing:".2px" };
export const hintStyle = { fontSize:11.5, color:"#64748b", marginTop:4, lineHeight:1.45 };
export const fieldErrorStyle = { fontSize:11.5, color:"#991b1b", marginTop:4, lineHeight:1.45 };
export const emptyRowStyle = { padding:36, textAlign:"center", color:"#94a3b8", fontSize:12 };
export const tableStyle = { width:"100%", borderCollapse:"collapse", fontSize:13 };
export const theadRowStyle = { background:"#fafbfc" };
export const rowBorder = (i) => ({ borderTop: i === 0 ? "none" : "1px solid #f1f5f9" });

// ── Presentational pieces ───────────────────────────────────────────

// An API failure as translated text: never err.message (which is the raw
// snake_case code). A code this screen doesn't know shows the generic
// message plus the code, for support.
export function FeedErrorText({ error }) {
  const { t } = useT();
  const code = feedErrorCode(error);
  return (
    <>
      {t(feedErrorKey(error))}
      {code && !isKnownFeedError(error) && <> {t("feed.err.codePrefix")}<code style={codeStyle}>{code}</code></>}
    </>
  );
}

export function Banner({ kind = "info", children, onDismiss, style }) {
  const { t } = useT();
  return (
    <div role={kind === "error" ? "alert" : "status"} style={{ ...bannerStyle(kind), display:"flex", gap:12, alignItems:"flex-start", ...(style || {}) }}>
      <div style={{ flex:1 }}>{children}</div>
      {onDismiss && (
        <button type="button" onClick={onDismiss}
          style={{ border:"none", background:"transparent", color:"inherit", cursor:"pointer", fontSize:12, fontWeight:700, fontFamily:"inherit", padding:0 }}>
          {t("feed.dismiss")}
        </button>
      )}
    </div>
  );
}

export function Chip({ tone, children }) {
  return <span style={chipStyle(tone)}>{children}</span>;
}
